import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRightAlt } from "@mui/icons-material";
import { useShareNoteMutation } from "../../../../../app/notesApiSlice";
import { useGetTeacherAssignedSubjectsQuery, useGetClassroomStudentsQuery } from "../../../../../app/teacherApiSlice";

const ShareNote = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const note = state?.note || null;

  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [shareNote, { isLoading: isSharingNote }] = useShareNoteMutation();

  // Fetch teacher's assigned classrooms
  const { data: teacherClasses = [] } = useGetTeacherAssignedSubjectsQuery();

  // Fetch classroom students (only when classroom is selected)
  const [selectedClassroomId, setSelectedClassroomId] = useState("");
  const { data: classroomStudents = [], isLoading: isLoadingStudents } = useGetClassroomStudentsQuery(
    { classroom_id: selectedClassroomId },
    { skip: !selectedClassroomId } // Skip query when no classroom is selected
  );

  // Extract unique classrooms
  const uniqueClassrooms = useMemo(() => {
    const classroomMap = {};
    teacherClasses.forEach((item) => {
      const classroomId = item.classroom_id ?? item.id;
      if (!classroomId || classroomMap[classroomId]) return;

      classroomMap[classroomId] = {
        id: String(classroomId),
        level: item.level,
        name: item.classroom ?? item.name,
      };
    });
    return Object.values(classroomMap);
  }, [teacherClasses]);

  useEffect(() => {
    if (!note) {
      navigate("/lms/notes");
    }
  }, [note, navigate]);

  const formik = useFormik({
    initialValues: {
      subject: note?.subject || "",
      level: note?.level || "",
      topic: note?.topic || "",
      body: note?.body || "",
      date: "",
      classroom: "",
      students: [],
      accessibility: "whole_class",
    },
    enableReinitialize: true,
    validationSchema: yup.object({
      subject: yup.string().required("Subject is required"),
      level: yup.string().required("Level is required"),
      topic: yup.string().required("Topic is required"),
      date: yup.string().required("Date is required"),
      classroom: yup.string().required("Please select a class to share to"),
      accessibility: yup.string().required("Accessibility option is required"),
      students: yup.array().when("accessibility", {
        is: (accessibility) => accessibility !== "whole_class",
        then: yup.array().min(1, "Select at least one student"),
        otherwise: yup.array(),
      }),
    }),
    onSubmit: async (values) => {
      try {
        const allStudentIds = classroomStudents.map(student => student.user_id ?? student.id);
        let studentIds = values.students;
        if (values.accessibility === "whole_class") {
          studentIds = allStudentIds;
        } else if (values.accessibility === "all_except") {
          studentIds = allStudentIds.filter((id) => !values.students.includes(id));
        }
        
        const shareData = {
          body: {
            date: values.date,
            classroom_id: values.classroom,
            students: studentIds,
          },
          id: note.id,
        };
        await shareNote(shareData).unwrap();
        setOpenSuccessModal(true);
        setTimeout(() => {
          navigate("/lms/notes");
        }, 1500);
      } catch (error) {
        console.error("Error sharing note:", error);
      }
    },
  });

  const handleClassroomChange = (event) => {
    const classroomId = event.target.value;
    formik.setFieldValue("classroom", classroomId);
    formik.setFieldValue("accessibility", "whole_class");
    formik.setFieldValue("students", []);
    setSelectedClassroomId(classroomId);
  };

  const handleAccessibilityChange = (event) => {
    const value = event.target.value;
    formik.setFieldValue("accessibility", value);
    if (value === "whole_class") {
      formik.setFieldValue("students", classroomStudents.map(student => student.user_id ?? student.id));
    } else {
      formik.setFieldValue("students", []);
    }
  };

  // Auto-populate all students when "Whole Class" accessibility is selected
  useEffect(() => {
    if (formik.values.accessibility === "whole_class" && classroomStudents?.length > 0) {
      const allStudentIds = classroomStudents.map(student => student.user_id ?? student.id);
      formik.setFieldValue("students", allStudentIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.accessibility, classroomStudents]);

  if (!note) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box component="form" onSubmit={formik.handleSubmit}>
        <Typography
          variant="h4"
          sx={{
            fontSize: { xs: "1rem", sm: "1.25rem" },
            fontWeight: 600,
            mb: 4,
          }}
        >
          Share Note
        </Typography>

        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={4}>
            <InputLabel
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "text.primary",
                marginBottom: "0.75em",
                textTransform: "uppercase",
              }}
            >
              Subject
            </InputLabel>
            <TextField
              id="subject"
              name="subject"
              value={formik.values.subject}
              fullWidth
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputLabel
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "text.primary",
                marginBottom: "0.75em",
                textTransform: "uppercase",
              }}
            >
              Level
            </InputLabel>
            <TextField
              id="level"
              name="level"
              value={formik.values.level}
              fullWidth
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <InputLabel
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "text.primary",
                marginBottom: "0.75em",
                textTransform: "uppercase",
              }}
            >
              Topic
            </InputLabel>
            <TextField
              id="topic"
              name="topic"
              value={formik.values.topic}
              fullWidth
              disabled
            />
          </Grid>

          <Grid item xs={12}>
            <InputLabel
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: "text.primary",
                marginBottom: "0.75em",
                textTransform: "uppercase",
              }}
            >
              Note Content (Preview)
            </InputLabel>
            <Box
              sx={{
                border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#ccc"}`,
                borderRadius: "4px",
                p: 2,
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "#1e1e1e" : "#f9f9f9",
                minHeight: "150px",
                maxHeight: "300px",
                overflow: "auto",
                "& p": { margin: "0 0 1em 0", fontSize: "0.95rem", lineHeight: "1.6" },
                "& h1, & h2, & h3, & h4, & h5, & h6": { marginTop: "0.75em", marginBottom: "0.5em", fontWeight: 600 },
                "& ul, & ol": { margin: "0 0 1em 0", paddingLeft: "1.5em" },
                "& li": { marginBottom: "0.5em" },
                "& code": { backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#f5f5f5", padding: "0.2em 0.4em", borderRadius: "3px", fontFamily: "monospace", fontSize: "0.9em" },
                "& pre": { backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#f5f5f5", padding: "1em", borderRadius: "4px", overflow: "auto", marginBottom: "1em" },
                "& blockquote": { borderLeft: (theme) => `3px solid ${theme.palette.mode === "dark" ? "#555" : "#ddd"}`, paddingLeft: "1em", marginLeft: 0, color: "text.secondary", fontStyle: "italic" },
                "& strong": { fontWeight: 600 },
                "& em": { fontStyle: "italic" },
              }}
              dangerouslySetInnerHTML={{ __html: formik.values.body }}
            />
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ mb: 4, border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}` }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", backgroundColor: "primary.main", color: "white", fontSize: "14px", fontWeight: 600 }}>
                    1
                  </Box>
                  Share Settings
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <TextField
                        id="date"
                        name="date"
                        label="Date to Share"
                        type="date"
                        value={formik.values.date}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.date && Boolean(formik.errors.date)}
                        helperText={formik.touched.date && formik.errors.date}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                            borderRadius: "12px",
                          },
                        }}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth error={formik.touched.classroom && Boolean(formik.errors.classroom)}>
                      <InputLabel>Class</InputLabel>
                      <Select
                        id="classroom"
                        name="classroom"
                        value={formik.values.classroom}
                        onChange={handleClassroomChange}
                        onBlur={formik.handleBlur}
                        label="Class"
                        sx={{
                          backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                          borderRadius: "12px",
                        }}
                      >
                        <MenuItem value="">
                          <em>Select a class</em>
                        </MenuItem>
                        {uniqueClassrooms.map((classroom) => (
                          <MenuItem key={classroom.id} value={classroom.id}>
                            {classroom.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.classroom && formik.errors.classroom && (
                        <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                          {formik.errors.classroom}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {formik.values.classroom && (
            <Grid item xs={12}>
              <Card sx={{ mb: 4, border: (theme) => `1px solid ${theme.palette.mode === "dark" ? "#444" : "#E0E0E0"}` }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", backgroundColor: "primary.main", color: "white", fontSize: "14px", fontWeight: 600 }}>
                      2
                    </Box>
                    Accessibility
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <FormControl fullWidth>
                        <InputLabel>Who can see this note?</InputLabel>
                        <Select
                          name="accessibility"
                          value={formik.values.accessibility}
                          onChange={handleAccessibilityChange}
                          onBlur={formik.handleBlur}
                          label="Who can see this note?"
                          disabled={isLoadingStudents}
                          sx={{
                            backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                            borderRadius: "12px",
                          }}
                        >
                          <MenuItem value="whole_class">Whole Class</MenuItem>
                          <MenuItem value="selected_students">Selected Students Only</MenuItem>
                          <MenuItem value="all_except">All Students Except Selected</MenuItem>
                        </Select>
                        {formik.touched.accessibility && formik.errors.accessibility && (
                          <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                            {formik.errors.accessibility}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>

                    {formik.values.accessibility !== "whole_class" && (
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>
                            {formik.values.accessibility === "selected_students"
                              ? "Select Students to Include"
                              : "Select Students to Exclude"}
                          </InputLabel>
                          <Select
                            multiple
                            name="students"
                            value={formik.values.students}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            label={
                              formik.values.accessibility === "selected_students"
                                ? "Select Students to Include"
                                : "Select Students to Exclude"
                            }
                            disabled={isLoadingStudents}
                            sx={{
                              backgroundColor: (theme) => theme.palette.mode === "dark" ? "#2a2a2a" : "#F9F9F9",
                              borderRadius: "12px",
                            }}
                            renderValue={(selected) => (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map((studentId) => {
                                  const student = classroomStudents.find((s) => (s.user_id ?? s.id) === studentId);
                                  return (
                                    <Chip
                                      key={studentId}
                                      label={student ? `${student.first_name} ${student.last_name}` : studentId}
                                      size="small"
                                    />
                                  );
                                })}
                              </Box>
                            )}
                          >
                            {classroomStudents.map((student) => {
                              const studentId = student.user_id ?? student.id;
                              return (
                                <MenuItem value={studentId} key={student.id}>
                                  <Checkbox
                                    checked={formik.values.students.includes(studentId)}
                                  />
                                  {student.first_name} {student.last_name}
                                </MenuItem>
                              );
                            })}
                          </Select>
                          {formik.touched.students && formik.errors.students && (
                            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
                              {formik.errors.students}
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        <Box
          sx={{
            margin: "3em 0 2em 0",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate("/lms/notes")}
            disabled={isSharingNote}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSharingNote || !formik.values.accessibility}
            endIcon={isSharingNote ? <CircularProgress size={20} /> : <ArrowRightAlt />}
          >
            {isSharingNote ? "Sharing..." : "Share Note"}
          </Button>
        </Box>
      </Box>

      {/* Success Modal */}
      <Dialog open={openSuccessModal} onClose={() => setOpenSuccessModal(false)}>
        <DialogTitle>Success</DialogTitle>
        <DialogContent>
          <Typography>Your note has been shared successfully!</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSuccessModal(false)}>OK</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShareNote;
